import React, { CSSProperties, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import type { IScreen } from 'src/modules/Redux';
import {
    getActiveScreen,
    getAudioStatus,
    getFont,
    getFontSize,
    getLayout,
    getOnlyShowFirstInitial,
    getVoice,
    getFulfillment,
    updateScreen,
    Font,
    Layout,
    Voice,
    voiceDetails,
    Fulfillment,
} from 'src/modules/Redux/slices/screen';
import { getShortId } from 'src/modules/Utils/helpers';
import { useWindowSize } from '../hooks/useWindowSize';
const borderColor = '#DFE0E2';

const styles: Record<string, CSSProperties> = {
    checkbox: {
        width: '20px',
        height: '20px',
        cursor: 'pointer',
        transform: 'scale(1.5)',
        margin: '0 auto',
    },
    submitButton: {
        backgroundColor: 'black',
        border: 'none',
        color: 'white',
        padding: '16px 32px',
        textAlign: 'center',
        display: 'inline-block',
        cursor: 'pointer',
        borderRadius: 8,
        fontWeight: 700,
        fontSize: 16,
    },
    settingsField: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '10px 0',
        borderBottom: '1px solid',
        borderColor: '#E5E5E5',
    },
    settingsLabel: {
        textAlign: 'left',
        fontWeight: 500,
        paddingTop: 16,
        paddingBottom: 16,
    },
    settingsControl: {
        width: '50%',
        textAlign: 'right',
    },
    label: {
        fontWeight: 500,
    },
    select: {
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        padding: '16px 32px',
        fontSize: '16px',
        color: 'black',
    },
};

const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const fontOptions = Object.entries(Font)
    .map(([key, value]) => ({
        label: key,
        value: value as Font,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const layoutOptions = Object.entries(Layout)
    .map(([key, value]) => ({
        label: capitalize(key),
        value: value as Layout,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const voiceOptions = Object.entries(Voice).map(([key, value]) => ({
    label: `${voiceDetails[value].name} (${voiceDetails[value].origin})`,
    value: value as Voice,
}));

// Fulfillment options for filtering orders based on purchase.fulfillment
export const fulfillmentOptions = [
    { label: 'Pickup', value: Fulfillment.PICKUP },
    { label: 'Dine In', value: Fulfillment.DINE_IN },
    { label: 'Delivery', value: Fulfillment.DELIVERY },
];

export const SettingsModal: React.FC<{
    isOpen: boolean;
    onRequestClose: () => void;
}> = ({ isOpen, onRequestClose }) => {
    const dispatch = useDispatch();

    const activeScreen = useSelector(getActiveScreen);
    const fontSizeFromStore = useSelector(getFontSize);
    const audioOnFromStore = useSelector(getAudioStatus);
    const voiceFromStore = useSelector(getVoice);
    const onlyShowFirstInitialFromStore = useSelector(getOnlyShowFirstInitial);
    const fulfillmentFromStore = useSelector(getFulfillment);
    
    const windowSize = useWindowSize();
    const isPortrait = windowSize.height && windowSize.width && windowSize.height > windowSize.width;
    
    const [onlyShowFirstInitial, setOnlyShowFirstInitialState] = useState(
        onlyShowFirstInitialFromStore
    );
    const [voice, setVoiceState] = useState(voiceFromStore);
    const layoutFromStore = useSelector(getLayout);
    const [layout, setLayoutState] = useState(layoutFromStore);
    const [fontSize, setFontSizeState] = useState(fontSizeFromStore);
    const [audioOn, setAudioOnState] = useState(audioOnFromStore);
    const fontFromStore = useSelector(getFont);
    const [font, setFontState] = useState(fontFromStore);
    
    const [fulfillment, setFulfillmentState] = useState<Fulfillment[]>(fulfillmentFromStore);
    
    const handleAudioToggle = () => {
        setAudioOnState(!audioOn);
    };
    const handleOnlyShowFirstInitialToggle = () => {
        setOnlyShowFirstInitialState(!onlyShowFirstInitial);
    };
    const handleFontChange = (event: any) => {
        setFontState(event.target.value);
    };
    const handleLayoutChange = (event: any) => {
        setLayoutState(event.target.value);
    };

    const handleFulfillmentChange = (fulfillmentType: Fulfillment) => {
        setFulfillmentState(prev => 
            prev.includes(fulfillmentType)
                ? prev.filter(f => f !== fulfillmentType)
                : [...prev, fulfillmentType]
        );
    };

    const handleSettingsSubmit = () => {
        if (!activeScreen) {
            onRequestClose();
            return;
        }

        const updates: Partial<IScreen> = {
            channelOptions: activeScreen.channelOptions?.map(channelOption => {
                if (channelOption.channel === activeScreen.channel) {
                    return {
                        channel: activeScreen.channelOptions?.[0]?.channel,
                        options: {
                            font,
                            fontSize,
                            layout,
                            voice,
                            audioOn,
                            onlyShowFirstInitial,
                            fulfillment,
                        },
                    };
                } else {
                    return channelOption;
                }
            }),
        };

        dispatch(
            updateScreen({
                screenId: activeScreen.id,
                updates,
            })
        );

        onRequestClose();
    };

    const handleVoiceChange = (event: any) => {
        setVoiceState(event.target.value);
    };

    const shortId = getShortId();
    return (
        // @ts-ignore
        <Modal
            isOpen={isOpen}
            onRequestClose={handleSettingsSubmit}
            appElement={document.getElementById('root') || undefined}
            style={{
                content: {
                    top: '50%',
                    left: '50%',
                    right: 'auto',
                    bottom: 'auto',
                    marginRight: '-50%',
                    transform: 'translate(-50%, -50%)',
                    padding: 0,
                    backgroundColor: 'white',
                    borderRadius: 8,
                    width: isPortrait ? '450px' : '800px',
                    minHeight: isPortrait ? '600px' : '500px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    borderColor,
                    paddingTop: 16,
                    fontSize: 16,
                },
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 100,
                },
            }}
        >
            <h2
                style={{
                    textAlign: 'center',
                    marginBottom: 16,
                }}
            >
                Settings
            </h2>
            <hr color={borderColor} />
            <div
                style={{
                    padding: 24,
                    flex: 1,
                }}
            >
                <div style={styles.settingsField}>
                    <div style={styles.settingsLabel}>
                        <label style={styles.label}>Audio On</label>
                    </div>
                    <div style={styles.settingsControl}>
                        <input
                            type="checkbox"
                            checked={audioOn}
                            onChange={handleAudioToggle}
                            style={styles.checkbox}
                        />
                    </div>
                </div>

                <div style={styles.settingsField}>
                    <div style={styles.settingsLabel}>
                        <label style={styles.label}>
                            Only Show Customers' First Initial
                        </label>
                    </div>
                    <div style={styles.settingsControl}>
                        <input
                            type="checkbox"
                            checked={onlyShowFirstInitial}
                            onChange={handleOnlyShowFirstInitialToggle}
                            style={styles.checkbox}
                        />
                    </div>
                </div>

                <div style={styles.settingsField}>
                    <div style={styles.settingsLabel}>
                        <label style={styles.label}>Voice</label>
                    </div>
                    <div style={styles.settingsControl}>
                        <select
                            style={styles.select}
                            value={voice}
                            onChange={handleVoiceChange}
                        >
                            {voiceOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div style={styles.settingsField}>
                    <div style={styles.settingsLabel}>
                        <label style={styles.label}>Fulfillment Types</label>
                    </div>
                    <div style={styles.settingsControl}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {fulfillmentOptions.map(option => (
                                <div key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={fulfillment.includes(option.value)}
                                        onChange={() => handleFulfillmentChange(option.value)}
                                        style={styles.checkbox}
                                    />
                                    <label>{option.label}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* <div style={styles.settingsField}>
                    <div style={styles.settingsLabel}>
                        <label style={styles.label}>Layout</label>
                    </div>
                    <div style={styles.settingsControl}>
                        <select
                            style={styles.select}
                            value={layout}
                            onChange={handleLayoutChange}
                        >
                            {layoutOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div> */}
                <div style={styles.settingsField}>
                    <div style={styles.settingsLabel}>
                        <label style={styles.label}>Font</label>
                    </div>
                    <div style={styles.settingsControl}>
                        <select
                            style={styles.select}
                            value={font}
                            onChange={handleFontChange}
                        >
                            {fontOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <br />
                <p>Device ID: {shortId}</p>
                <br />
                <br />
                <div
                    style={{
                        flexDirection: 'row',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <button
                        style={styles.submitButton}
                        onClick={handleSettingsSubmit}
                    >
                        Submit
                    </button>
                    <br />
                    <p
                        onClick={() => {
                            window.location.reload();
                        }}
                        style={{
                            color: 'red',
                            fontWeight: 500,
                        }}
                    >
                        Restart
                    </p>
                </div>
            </div>
        </Modal>
    );
};
